import React, { useState } from "react";
import { Link } from "react-router-dom";
import backArrow from "../assets/images/arrow_back.svg";
import GenZLandingPageWallpaper from "../assets/images/GenZLandingPageWallpaper.svg";
import * as XLSX from 'xlsx';
import axios from "axios";
import CryptoJS from "crypto-js";
import { saveAs } from 'file-saver';

const UserImport = () => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  
  const [useEmailAsUsername, setUseEmailAsUsername] = useState(false);
  const [useFixedPassword, setUseFixedPassword] = useState(false);
  const [fixedPassword, setFixedPassword] = useState("");
  const [isSocialGlassMode, setIsSocialGlassMode] = useState(false);
  // In Social Glass mode, we don't use defaults; fields must be in the CSV
  
  const generatePassword = () => {
    const length = 10;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    
    password += "A";
    password += "a";
    password += "1";
    password += "!";
    
    for (let i = 4; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError("");
    setResults(null);
  };
  
  const setSocialGlassDefaults = () => {
    setUseEmailAsUsername(true);
    setUseFixedPassword(true);
    setFixedPassword("SocialGlass2023!");
    setIsSocialGlassMode(true);
  };

  const processFile = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }
    
    if (useFixedPassword && !fixedPassword) {
      setError("Please enter a fixed password");
      return;
    }
    
    setIsLoading(true);
    setProgress(0);
    
    try {
      const data = await readExcelFile(file);
      
      if (!data || data.length === 0) {
        throw new Error("No data found in file or invalid format");
      }
      
      const requiredFields = ['firstName', 'lastName', 'email'];
      const missingFields = requiredFields.filter(field => 
        !data[0].hasOwnProperty(field.toLowerCase()) && 
        !data[0].hasOwnProperty(field)
      );
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
      
      const tokenResponse = await axios.post("https://genz-staging.feeltiptop.com/api/token", {
        passCode: "4zPoQ5KAkR",
      });
      
      const token = tokenResponse.data.access_token;
      
      const processedUsers = [];
      let successCount = 0;
      let failCount = 0;
      
      for (let i = 0; i < data.length; i++) {
        const user = data[i];
        const progress = Math.round(((i + 1) / data.length) * 100);
        setProgress(progress);
        
        try {
          const normalizedUser = {};
          Object.keys(user).forEach(key => {
            normalizedUser[key.toLowerCase()] = user[key];
          });
          
          const firstName = normalizedUser.firstname || "";
          const lastName = normalizedUser.lastname || "";
          const email = normalizedUser.email || "";
          
          // const paymentType = normalizedUser.paymenttype;
          // const paymentInfo = normalizedUser.paymentinfo;
          
          let username;
          if (useEmailAsUsername) {
            username = email;
          } else {
            const baseUsername = `${firstName.toLowerCase()}${lastName.toLowerCase()}`;
            const randomSuffix = Math.floor(Math.random() * 1000);
            username = `${baseUsername}${randomSuffix}`;
          }
          
          let password;
          if (useFixedPassword) {
            password = fixedPassword;
          } else {
            password = generatePassword();
          }
          
          const hashedPassword = CryptoJS.SHA256(password).toString();
          
          // Create user data object based on mode
          let userData; // Use let to allow modification
          
          if (isSocialGlassMode) {
            // Start with base Social Glass fields
            userData = {
              userName: username,
              firstName: firstName,
              lastName: lastName,
              email: email,
              password: hashedPassword,
              activated: false
            };
            // Conditionally add payment info if present in the row
            if (normalizedUser.hasOwnProperty('paymenttype') && normalizedUser.paymenttype) {
              // Optional: Add validation if needed, e.g., check if it's 'venmo' or 'paypal'
              userData.paymentType = normalizedUser.paymenttype;
            }
            if (normalizedUser.hasOwnProperty('paymentinfo') && normalizedUser.paymentinfo) {
              userData.paymentInfo = normalizedUser.paymentinfo;
            }
          } else {
            // Regular user data (unchanged)
            userData = {
              userName: username,
              firstName: firstName,
              lastName: lastName,
              email: email,
              password: hashedPassword,
              phoneNumber: normalizedUser.phonenumber || null, 
              termsAndConditions: "Accepted",
              textMessaging: "Consented",
              selectedCard: "360 Buyer Card ($100)", 
              paymentAmount: "0",
              status: "Deactivated",
              activated: false  
            };
          }
          
          await axios.post(
            isSocialGlassMode 
              ? "https://genz-staging.feeltiptop.com/api/social_glass/addUser"
              : "https://genz-staging.feeltiptop.com/api/user/addUser",
            userData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          processedUsers.push({
            ...userData,
            password: password 
          });
          
          successCount++;
        } catch (userError) {
          console.error(`Error creating user ${user.email}:`, userError);
          processedUsers.push({
            ...user,
            error: userError.message || "Unknown error"
          });
          failCount++;
        }
      }
      
      setResults({
        total: data.length,
        success: successCount,
        fail: failCount,
        users: processedUsers
      });
      
    } catch (err) {
      console.error("Error processing file:", err);
      setError(err.message || "An error occurred while processing the file");
    } finally {
      setIsLoading(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.readAsBinaryString(file);
    });
  };

  const downloadResults = () => {
    if (!results) return;
    
    let csvContent = "Username,Password,FirstName,LastName,Email,PaymentType,PaymentInfo,Status\n";
    
    results.users.forEach(user => {
      const escapeCsv = (field) => {
        if (field === undefined || field === null) return '';
        const str = String(field);
        return str.includes(',') || str.includes('"') 
          ? `"${str.replace(/"/g, '""')}"` 
          : str;
      };
      
      const row = [
        escapeCsv(user.userName),
        escapeCsv(user.password),
        escapeCsv(user.firstName),
        escapeCsv(user.lastName),
        escapeCsv(user.email),
        escapeCsv(user.paymentType || ''),
        escapeCsv(user.paymentInfo || ''),
        user.error ? `Error: ${escapeCsv(user.error)}` : 'Deactivated'
      ];
      
      csvContent += row.join(',') + '\n';
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, "imported_users_with_passwords.csv");
  };

  const CustomRadio = ({ id, name, checked, onChange, disabled, children }) => (
    <label 
      htmlFor={id}
      className={`flex items-center space-x-3 mb-2 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className="relative">
        <input
          id={id}
          type="radio"
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="opacity-0 absolute h-5 w-5"
        />
        <div className={`border-2 rounded-full h-5 w-5 flex flex-shrink-0 justify-center items-center mr-2 ${checked ? 'border-blue-500' : 'border-gray-300'}`}>
          <div className={`rounded-full h-3 w-3 flex items-center justify-center transition-all ${checked ? 'bg-blue-500' : 'bg-transparent'}`}></div>
        </div>
      </div>
      <div className="select-none">{children}</div>
    </label>
  );

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: `url(${GenZLandingPageWallpaper})` }}
    >
      <div className="z-10 w-full max-w-2xl rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 flex items-center justify-between border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-800">
            Import Users from Spreadsheet
          </h2>
          <button 
            onClick={isSocialGlassMode ? () => {
              setIsSocialGlassMode(false);
              setDefaultPaymentType("venmo");
              setDefaultPaymentInfo("");
            } : setSocialGlassDefaults}
            disabled={isLoading}
            className={`rounded-md px-4 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isSocialGlassMode 
                ? "bg-blue-100 text-blue-700 hover:bg-blue-200 focus:ring-blue-400" 
                : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200 focus:ring-indigo-400"
            }`}
          >
            {isSocialGlassMode ? "Switch to Normal Mode" : "Social Glass Mode"}
          </button>
        </div>
        
        <div className="mb-8">
          <h3 className="mb-4 text-lg font-semibold text-gray-700">User Creation Options</h3>
          
          <div className="mb-6">
            <p className="mb-3 font-medium text-gray-700">Username Format</p>
            <div className="space-y-2 pl-2">
              <CustomRadio
                id="usernameFormat1"
                name="usernameFormat"
                checked={!useEmailAsUsername}
                onChange={() => setUseEmailAsUsername(false)}
                disabled={isLoading}
              >
                <span className="text-gray-800">FirstName + LastName + Random Numbers</span>
                <p className="text-xs text-gray-500">Example: johndoe123</p>
              </CustomRadio>
              
              <CustomRadio
                id="usernameFormat2"
                name="usernameFormat"
                checked={useEmailAsUsername}
                onChange={() => setUseEmailAsUsername(true)}
                disabled={isLoading}
              >
                <span className="text-gray-800">Use Email as Username</span>
                <p className="text-xs text-gray-500">Example: john.doe@example.com</p>
              </CustomRadio>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="mb-3 font-medium text-gray-700">Password Type</p>
            <div className="space-y-2 pl-2">
              <CustomRadio
                id="passwordType1"
                name="passwordType"
                checked={!useFixedPassword}
                onChange={() => setUseFixedPassword(false)}
                disabled={isLoading}
              >
                <span className="text-gray-800">Unique Random Password</span>
                <p className="text-xs text-gray-500">Includes uppercase, lowercase, number, and symbol</p>
              </CustomRadio>
              
              <CustomRadio
                id="passwordType2"
                name="passwordType"
                checked={useFixedPassword}
                onChange={() => setUseFixedPassword(true)}
                disabled={isLoading}
              >
                <span className="text-gray-800">Fixed Password for All Users</span>
              </CustomRadio>
            </div>
            
            {useFixedPassword && (
              <div className="mt-3">
                <input
                  type="text"
                  placeholder="Enter fixed password"
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={fixedPassword}
                  onChange={(e) => setFixedPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
          
          {/* New Payment Type and Payment Info Fields */}
      // Payment fields are not used in normal mode
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Payment Information</h3>
        
        {isSocialGlassMode ? (
          <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Social Glass Mode Requirements</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>In Social Glass mode, the imported CSV <strong>must include</strong> the following columns:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li><strong>paymentType</strong> - Must be exactly "venmo" or "paypal"</li>
                    <li><strong>paymentInfo</strong> - Phone number for Venmo or email for PayPal</li>
                  </ul>
                  <p className="mt-2">These fields are required for all users and cannot be set as defaults.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600 mb-4">
            Optional columns in your CSV: <span className="font-medium">paymentType</span> (venmo/paypal), <span className="font-medium">paymentInfo</span> (phone number for Venmo, email for PayPal)
          </p>
        )}
      </div>
        </div>
        
        <div className="mb-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-700">Select File to Import</h3>
          <p className="mb-2 text-sm text-gray-600">
            Upload a CSV or Excel file with user data. Required columns: <span className="font-medium">firstName</span>, <span className="font-medium">lastName</span>, <span className="font-medium">email</span>.
          </p>
          <p className="mb-2 text-sm text-gray-600">
            Optional columns: <span className="font-medium">phoneNumber</span>, <span className="font-medium">paymentType</span> (venmo/paypal{isSocialGlassMode ? "/social_glass" : ""}), <span className="font-medium">paymentInfo</span> (phone or email).
          </p>
          {isSocialGlassMode && (
            <p className="mb-2 text-sm text-red-600 font-semibold">
              Note: In Social Glass mode, payment info is required for all users.
            </p>
          )}
          
          <div className="mt-4 rounded-lg border-2 border-dashed border-gray-300 p-6 transition hover:border-gray-400">
            <div className="flex flex-col items-center justify-center">
              <svg className="mb-2 h-10 w-10 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              
              <p className="mb-2 text-sm font-medium text-gray-700">
                {file ? file.name : "Drag and drop your file or click to browse"}
              </p>
              
              <p className="text-xs text-gray-500">
                Supported formats: CSV, XLS, XLSX
              </p>
              
              <input
                id="fileInput"
                type="file"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                className="hidden"
                onChange={handleFileChange}
                disabled={isLoading}
              />
              
              <button
                onClick={() => document.getElementById('fileInput').click()}
                disabled={isLoading}
                className="mt-4 rounded-md bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-200 focus:outline-none"
              >
                Select File
              </button>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4 text-red-700">
            <div className="flex">
              <svg className="mr-3 h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">Error:</p>
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}
        
        {isLoading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Processing users...</p>
              <p className="text-sm font-medium text-gray-700">{progress}%</p>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div 
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}
        
        {results && (
          <div className="mb-6 rounded-md bg-green-50 p-4 text-green-700">
            <div className="flex items-start">
              <svg className="mr-3 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586V13a1 1 0 11-2 0V5.414l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium">Import Results:</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <div className="rounded-md bg-white p-3 shadow-sm">
                    <p className="text-xs text-gray-500">Total Records</p>
                    <p className="text-xl font-bold">{results.total}</p>
                  </div>
                  <div className="rounded-md bg-white p-3 shadow-sm">
                    <p className="text-xs text-gray-500">Successful</p>
                    <p className="text-xl font-bold text-green-600">{results.success}</p>
                  </div>
                  <div className="rounded-md bg-white p-3 shadow-sm">
                    <p className="text-xs text-gray-500">Failed</p>
                    <p className="text-xl font-bold text-red-600">{results.fail}</p>
                  </div>
                </div>
                <button
                  onClick={downloadResults}
                  className="mt-4 flex items-center rounded bg-green-600 px-4 py-2 text-white transition hover:bg-green-700"
                >
                  <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download User Credentials
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-center">
          <button
            onClick={processFile}
            disabled={!file || isLoading}
            className={`rounded-md px-6 py-3 text-white shadow-md transition focus:outline-none focus:ring-2 focus:ring-offset-2 
            ${!file || isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'}`}
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="mr-2 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                Import Users
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserImport;